'use client';

import { useTranslations } from 'next-intl';
import { CacheKeys, ServerActionName } from '@headless-workspace/config';
import { AsyncData, Result, ResultEmpty, ResultFailure } from '@headless-workspace/core-domain';
import useSWRMutation, { TriggerWithoutArgs } from 'swr/mutation';
import {
    FavoriteStoreErrors,
    FavoriteStoreUpdateMode,
    getFavoriteStoreServerAction,
    RevalidationStrategy,
    StoreInfoValue,
    updateFavoriteStoreServerAction,
    useSafeSWR,
} from '../../../../../src';
import { CommonDI, UserCookiesRepositoryType } from '../../../../DI';

export type MutationTriggerReturnType = TriggerWithoutArgs<
    Result<StoreInfoValue>,
    unknown,
    typeof CacheKeys.FavoriteStore
>;

type UseFavoriteStoreInfoReturnType = AsyncData<StoreInfoValue> & {
    deleteFavoriteStore: MutationTriggerReturnType;
};

/**
 * useFavoriteStoreInfo
 * @param hostUrl The host URL needed when deleting the cookie
 * @returns An object containing the store information and a function to delete the favorite store
 */
export const useFavoriteStoreInfo = (hostUrl?: string): UseFavoriteStoreInfoReturnType => {
    const t = useTranslations('Header.storesAndServices.banner.error');

    const { data, isLoading, error } = useSafeSWR(
        CacheKeys.FavoriteStore,
        ServerActionName.getFavoriteStore,
        () => getFavoriteStoreServerAction(),
        RevalidationStrategy.HotDataPolicy,
    );
    const { trigger: triggerDeleteFavoriteStore } = useSWRMutation<
        Result<StoreInfoValue>,
        unknown,
        typeof CacheKeys.FavoriteStore
    >(CacheKeys.FavoriteStore, async (url, { arg }) => {
        void CommonDI.get(UserCookiesRepositoryType).deleteFavoriteStoreCookie(hostUrl ?? '');
        return updateFavoriteStoreServerAction(FavoriteStoreUpdateMode.Remove);
    });

    if (!data) {
        if (error) {
            return {
                isLoading,
                result: ResultFailure(
                    FavoriteStoreErrors.Default,
                    error instanceof Error ? error.message : t('defaultError'),
                ),
                deleteFavoriteStore: triggerDeleteFavoriteStore,
            };
        }

        return { isLoading, result: ResultEmpty(), deleteFavoriteStore: triggerDeleteFavoriteStore };
    }

    return { isLoading, result: data, deleteFavoriteStore: triggerDeleteFavoriteStore };
};
